import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { getDebugStatus, type DebugStatusResult } from "@/lib/admin.debug.functions";
import { Eyebrow } from "@/components/editorial";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/debug")({
  head: () => ({
    meta: [
      { title: "Debug — Tiger's Eye" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminDebug,
});

interface ClientError {
  id: string;
  at: number;
  source: "window-error" | "unhandled-rejection";
  name: string;
  message: string;
  stack?: string;
  route?: string;
}

const CLIENT_KEY = "tigerseye:client-error-buffer";
const CLIENT_RING = 50;

function readClientBuffer(): ClientError[] {
  if (typeof sessionStorage === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(CLIENT_KEY);
    return raw ? (JSON.parse(raw) as ClientError[]) : [];
  } catch {
    return [];
  }
}

function clearClientBuffer() {
  try {
    sessionStorage.removeItem(CLIENT_KEY);
  } catch {
    /* ignore */
  }
}

function formatTime(ts: number): string {
  return new Date(ts).toISOString().replace("T", " ").replace("Z", "");
}

function relative(ts: number, now: number): string {
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

function formatUptime(ms: number): string {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rem = s % 60;
  return `${h}h ${m}m ${rem}s`;
}

function AdminDebug() {
  const fn = useServerFn(getDebugStatus);
  const q = useQuery<DebugStatusResult>({
    queryKey: ["admin-debug-status"],
    queryFn: () => fn(),
    refetchInterval: 10_000,
  });

  const [clientErrors, setClientErrors] = useState<ClientError[]>([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setClientErrors(readClientBuffer());
    const i = setInterval(() => {
      setClientErrors(readClientBuffer());
      setTick((t) => t + 1);
    }, 5_000);
    return () => clearInterval(i);
  }, []);

  const now = useMemo(() => Date.now(), [q.dataUpdatedAt, tick]);
  const status = q.data?.status;
  const serverErrors = q.data?.errors ?? [];

  return (
    <div className="space-y-10">
      <header>
        <Eyebrow>Debug</Eyebrow>
        <h2 className="font-serif text-3xl text-primary mt-1">SSR & runtime diagnostics</h2>
        <p className="text-sm text-ink-secondary mt-2 max-w-2xl">
          Per-isolate snapshot. Cloudflare Workers may spread requests across many isolates,
          so this is a sample, not a complete log. For exhaustive history, check the published
          worker logs in the platform dashboard.
        </p>
      </header>

      <section className="border border-rule bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <Eyebrow>SSR status</Eyebrow>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-2 text-xs font-mono ${q.isError ? "text-destructive" : "text-emerald-700"}`}
            >
              <span
                className={`size-2 rounded-full ${q.isError ? "bg-destructive" : "bg-emerald-500"}`}
              />
              {q.isError ? "unreachable" : q.isFetching ? "refreshing" : "healthy"}
            </span>
            <Button size="sm" variant="outline" onClick={() => q.refetch()}>
              Refresh
            </Button>
          </div>
        </div>
        {status ? (
          <dl className="grid gap-2 sm:grid-cols-[10rem_1fr] text-sm">
            <dt className="text-ink-tertiary uppercase text-[0.7rem] tracking-[0.08em]">Runtime</dt>
            <dd className="font-mono">{status.runtime}</dd>
            <dt className="text-ink-tertiary uppercase text-[0.7rem] tracking-[0.08em]">Booted</dt>
            <dd className="font-mono">
              {formatTime(status.bootedAt)} ({relative(status.bootedAt, status.now)})
            </dd>
            <dt className="text-ink-tertiary uppercase text-[0.7rem] tracking-[0.08em]">Uptime</dt>
            <dd className="font-mono">{formatUptime(status.uptimeMs)}</dd>
            <dt className="text-ink-tertiary uppercase text-[0.7rem] tracking-[0.08em]">Errors held</dt>
            <dd className="font-mono">
              {status.errorCount} / {status.bufferCapacity}
            </dd>
          </dl>
        ) : (
          <p className="text-sm text-ink-tertiary">
            {q.isLoading ? "Loading…" : q.error instanceof Error ? q.error.message : "No data."}
          </p>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <Eyebrow>Server errors (this isolate)</Eyebrow>
          <span className="text-xs text-ink-tertiary font-mono">{serverErrors.length} entries</span>
        </div>
        <ErrorList items={serverErrors} now={now} emptyLabel="No SSR errors in this isolate." />
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <Eyebrow>Client errors (this session)</Eyebrow>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-tertiary font-mono">{clientErrors.length} entries</span>
            {clientErrors.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  clearClientBuffer();
                  setClientErrors([]);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
        <ErrorList
          items={clientErrors.slice().reverse()}
          now={now}
          emptyLabel="No client-side errors captured this session."
        />
      </section>
    </div>
  );
}

interface DisplayItem {
  id: string;
  at: number;
  source: string;
  name: string;
  message: string;
  stack?: string;
  requestId?: string;
  route?: string;
  method?: string;
}

function ErrorList({
  items,
  now,
  emptyLabel,
}: {
  items: DisplayItem[];
  now: number;
  emptyLabel: string;
}) {
  if (items.length === 0) {
    return (
      <div className="border border-dashed border-rule bg-card/40 p-8 text-center text-sm text-ink-tertiary">
        {emptyLabel}
      </div>
    );
  }
  return (
    <ul className="divide-y divide-rule border border-rule bg-card">
      {items.map((e) => (
        <li key={e.id} className="p-4">
          <details>
            <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="font-mono text-sm text-destructive truncate">
                  {e.name}: {e.message}
                </p>
                <p className="text-[0.7rem] text-ink-tertiary mt-1 font-mono uppercase tracking-[0.08em]">
                  {e.source}
                  {e.method ? ` · ${e.method}` : ""}
                  {e.route ? ` · ${e.route}` : ""}
                  {e.requestId ? ` · req ${e.requestId}` : ""}
                </p>
              </div>
              <span className="text-xs text-ink-tertiary font-mono whitespace-nowrap shrink-0">
                {relative(e.at, now)}
              </span>
            </summary>
            <div className="mt-3 space-y-2 text-xs">
              <p className="text-ink-tertiary font-mono">{formatTime(e.at)}</p>
              {e.stack ? (
                <pre className="bg-muted/40 border border-rule p-3 font-mono text-[0.72rem] leading-relaxed text-destructive whitespace-pre-wrap break-words max-h-72 overflow-auto">
                  {e.stack}
                </pre>
              ) : (
                <p className="text-ink-tertiary italic">No stack trace.</p>
              )}
            </div>
          </details>
        </li>
      ))}
    </ul>
  );
}
